import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { laundryItems, defaultExecutionTimes, type LaundryItem, type ServicePrice } from '@/mocks/items';
import { packagesList, type LaundryPackage } from '@/mocks/packages';
import { driversList } from '@/mocks/drivers';
import { ordersList, type Order, type OrderStatus } from '@/mocks/orders';
import { dashboardAlerts } from '@/mocks/dashboard';

export type ServiceKey = 'washOnly' | 'ironOnly' | 'washIron';
export type Driver = (typeof driversList)[number];

export interface ExecutionTimes {
  normal: number;
  urgent: number;
}

export interface DashboardAlert {
  id: string;
  orderNo: string;
  title: string;
  note: string;
  waitingFor: string;
}

export interface DriverFormData {
  name: string;
  phone: string;
  username: string;
  password: string;
  photo: string;
  idDoc: string;
  licenseDoc: string;
  vehiclePhoto: string;
}

export type NewItemData = Omit<LaundryItem, 'id'>;
export type NewPackageData = Omit<LaundryPackage, 'id' | 'subscribers'>;

const EVENT_STAMP = { date: '2026-09-16', time: 'الآن' };

const DEFAULT_ITEM_IMAGE =
  'https://readdy.ai/api/search-image?query=Clean%20minimal%20laundry%20item%20placeholder%20on%20a%20soft%20light%20gray%20studio%20background%2C%20folded%20neutral%20fabric%2C%20centered%20product%20photography%2C%20soft%20even%20lighting&width=400&height=400&seq=item-default-fallback-01&orientation=squarish';

const DEFAULT_DRIVER_PHOTO =
  'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20friendly%20delivery%20driver%20in%20a%20clean%20uniform%2C%20neutral%20light%20studio%20background%2C%20soft%20even%20lighting%2C%20corporate%20identity%20photo&width=200&height=200&seq=driver-default-fallback-01&orientation=squarish';

interface AppDataValue {
  items: LaundryItem[];
  executionTimes: ExecutionTimes;
  saveExecutionTimes: (times: ExecutionTimes) => void;
  patchItem: (id: string, patch: Partial<LaundryItem>) => void;
  patchService: (id: string, service: ServiceKey, patch: Partial<ServicePrice>) => void;
  addItem: (data: NewItemData) => void;
  updateItem: (id: string, data: NewItemData) => void;
  deleteItem: (id: string) => void;

  packages: LaundryPackage[];
  addPackage: (data: NewPackageData) => void;
  updatePackage: (id: string, data: NewPackageData) => void;
  deletePackage: (id: string) => void;
  togglePackageActive: (id: string) => void;

  drivers: Driver[];
  addDriver: (data: DriverFormData) => void;
  updateDriver: (id: string, data: DriverFormData) => void;
  toggleDriverStatus: (id: string) => void;
  resetDriverPassword: (id: string) => void;

  orders: Order[];
  patchOrderStatus: (id: string, status: OrderStatus, label: string) => void;
  assignOrderDriver: (id: string, driverId: string, driverName: string) => void;

  alerts: DashboardAlert[];
  removeAlert: (id: string) => void;
}

const AppDataContext = createContext<AppDataValue | null>(null);

export function AppDataProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<LaundryItem[]>(laundryItems);
  const [executionTimes, setExecutionTimes] = useState<ExecutionTimes>(defaultExecutionTimes);
  const [packages, setPackages] = useState<LaundryPackage[]>(packagesList);
  const [drivers, setDrivers] = useState<Driver[]>(driversList as Driver[]);
  const [orders, setOrders] = useState<Order[]>(ordersList);
  const [alerts, setAlerts] = useState<DashboardAlert[]>(dashboardAlerts);

  const value = useMemo<AppDataValue>(() => {
    const saveExecutionTimes = (times: ExecutionTimes) => setExecutionTimes(times);

    const patchItem = (id: string, patch: Partial<LaundryItem>) =>
      setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));

    const patchService = (id: string, service: ServiceKey, patch: Partial<ServicePrice>) =>
      setItems((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, [service]: { ...(item[service] as ServicePrice), ...patch } }
            : item
        )
      );

    const addItem = (data: NewItemData) =>
      setItems((prev) => [
        { id: `ITM-${Date.now()}`, ...data, image: data.image || DEFAULT_ITEM_IMAGE },
        ...prev,
      ]);

    const updateItem = (id: string, data: NewItemData) =>
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, ...data, image: data.image || item.image || DEFAULT_ITEM_IMAGE } : item
        )
      );

    const deleteItem = (id: string) => setItems((prev) => prev.filter((item) => item.id !== id));

    const addPackage = (data: NewPackageData) =>
      setPackages((prev) => [{ id: `PKG-${Date.now()}`, ...data, subscribers: [] }, ...prev]);

    const updatePackage = (id: string, data: NewPackageData) =>
      setPackages((prev) => prev.map((pkg) => (pkg.id === id ? { ...pkg, ...data } : pkg)));

    const deletePackage = (id: string) => setPackages((prev) => prev.filter((pkg) => pkg.id !== id));

    const togglePackageActive = (id: string) =>
      setPackages((prev) =>
        prev.map((pkg) => (pkg.id === id ? { ...pkg, active: !pkg.active } : pkg))
      );

    const addDriver = (data: DriverFormData) =>
      setDrivers((prev) => [
        {
          id: `DRV-${Date.now()}`,
          name: data.name,
          phone: data.phone,
          status: 'available',
          statusLabel: 'متاح',
          distance: '—',
          tasksToday: 0,
          rating: 0,
          vehicle: '—',
          photo: DEFAULT_DRIVER_PHOTO,
        } as Driver,
        ...prev,
      ]);

    const updateDriver = (id: string, data: DriverFormData) =>
      setDrivers((prev) =>
        prev.map((driver) =>
          driver.id === id ? { ...driver, name: data.name, phone: data.phone } : driver
        )
      );

    const toggleDriverStatus = (id: string) =>
      setDrivers((prev) =>
        prev.map((driver) =>
          driver.id === id
            ? {
                ...driver,
                status: driver.status === 'disabled' ? 'available' : 'disabled',
                statusLabel: driver.status === 'disabled' ? 'متاح' : 'معطّل',
              }
            : driver
        )
      );

    const resetDriverPassword = () => {
      /* mock — actual reset handled by backend in production */
    };

    const patchOrderStatus = (id: string, status: OrderStatus, label: string) =>
      setOrders((prev) =>
        prev.map((order) =>
          order.id === id
            ? {
                ...order,
                status,
                history: [...order.history, { status, label, ...EVENT_STAMP }],
              }
            : order
        )
      );

    const assignOrderDriver = (id: string, driverId: string, driverName: string) =>
      setOrders((prev) =>
        prev.map((order) =>
          order.id === id
            ? {
                ...order,
                driverId,
                status: 'processing',
                history: [
                  ...order.history,
                  { status: 'processing', label: `تم تعيين المندوب ${driverName}`, ...EVENT_STAMP },
                ],
              }
            : order
        )
      );

    const removeAlert = (id: string) => setAlerts((prev) => prev.filter((alert) => alert.id !== id));

    return {
      items,
      executionTimes,
      saveExecutionTimes,
      patchItem,
      patchService,
      addItem,
      updateItem,
      deleteItem,
      packages,
      addPackage,
      updatePackage,
      deletePackage,
      togglePackageActive,
      drivers,
      addDriver,
      updateDriver,
      toggleDriverStatus,
      resetDriverPassword,
      orders,
      patchOrderStatus,
      assignOrderDriver,
      alerts,
      removeAlert,
    };
  }, [items, executionTimes, packages, drivers, orders, alerts]);

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData() {
  const context = useContext(AppDataContext);
  if (!context) throw new Error('useAppData must be used within AppDataProvider');
  return context;
}